import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Sparkles, FileText, Loader2, RotateCcw } from "lucide-react";
import { enhanceCvForAssignment, fetchEmployeeCvsForAdmin } from "../api/TheirStackJobsApi";
import { listDocuments } from "features/cv-processor/api/CvProcessorApi";
import type { CvDocument } from "features/cv-processor/api/CvProcessorApi";
import EnhancedCvModal from "features/cv-processor/ui/EnhancedCvModal";
import EnhancementLogsModal from "./EnhancementLogsModal";

type Phase = "idle" | "enhancing" | "done" | "error";

interface EnhancementPatch {
  has_enhancement: boolean;
  cv_id_enhanced: number;
  enhanced_cv_data: any;
  has_enhancement_logs: boolean;
}

export interface CvEnhancementSectionHandle {
  runEnhancement: () => Promise<void>;
  canEnhance: boolean;
}

interface Props {
  assignmentId: number;
  isAdmin: boolean;
  employeeCode?: string; // admin must supply
  initialEnhancedData?: any;
  cvIdEnhanced?: number | null;
  hasEnhancementLogs?: boolean;
  jobTitle: string;
  jobDescription?: string;
  onEnhanced?: (patch: EnhancementPatch) => void;
  /** When false, hides this section's own idle-state "Enhance" button — used when a
   * parent wraps this alongside CoverLetterEnhancementSection under one combined
   * "Enhance All" trigger. Defaults to true so standalone usage is unchanged. */
  showOwnTrigger?: boolean;
  /** Notifies the parent whenever the selected CV changes, so a sibling section
   * (e.g. cover letter) can auto-match on the CV's category_id. */
  onCvSelected?: (cvId: number | "", categoryId: number | null) => void;
}

const CvEnhancementSection = forwardRef<CvEnhancementSectionHandle, Props>(function CvEnhancementSection({
  assignmentId,
  isAdmin,
  employeeCode,
  initialEnhancedData,
  cvIdEnhanced,
  hasEnhancementLogs,
  jobTitle,
  jobDescription,
  onEnhanced,
  showOwnTrigger = true,
  onCvSelected,
}, ref) {
  const [phase, setPhase] = useState<Phase>(initialEnhancedData ? "done" : "idle");
  const [cvList, setCvList] = useState<{ id: number; title: string; category_id?: number | null }[] | null>(null);
  const [cvLoading, setCvLoading] = useState(false);
  const [selectedCvId, setSelectedCvId] = useState<number | "">("");
  const [enhancedData, setEnhancedData] = useState<CvDocument | null>(initialEnhancedData ?? null);
  const [showCvModal, setShowCvModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [hasLogs, setHasLogs] = useState(hasEnhancementLogs ?? false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  // Sync initialEnhancedData if parent re-fetches detail
  useEffect(() => {
    if (initialEnhancedData && (phase === "idle" || phase === "error")) {
      setEnhancedData(initialEnhancedData as CvDocument);
      setPhase("done");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEnhancedData]);

  // Load CV list
  useEffect(() => {
    if (isAdmin && !employeeCode) return;
    setCvLoading(true);
    const load = isAdmin
      ? fetchEmployeeCvsForAdmin(employeeCode!)
      : listDocuments().then((docs) => docs.map((d) => ({ id: d.id, title: d.title, category_id: d.category_id })));
    load
      .then((list) => { if (mountedRef.current) { setCvList(list); setCvLoading(false); } })
      .catch(() => { if (mountedRef.current) { setCvList([]); setCvLoading(false); } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, employeeCode]);

  // Notify parent whenever the selected CV (or its list) changes, so a sibling
  // cover-letter section can auto-match by category_id.
  useEffect(() => {
    if (!onCvSelected) return;
    if (selectedCvId === "") { onCvSelected("", null); return; }
    const cv = (cvList ?? []).find((c) => c.id === selectedCvId);
    onCvSelected(selectedCvId, cv?.category_id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCvId, cvList]);

  async function runEnhancement(cvId: number) {
    if (!cvId) return;
    if (isAdmin && !employeeCode) return;
    setPhase("enhancing");
    setError(null);
    try {
      const result = await enhanceCvForAssignment(
        assignmentId,
        cvId,
        isAdmin,
        isAdmin ? employeeCode : undefined,
      );
      if (mountedRef.current) {
        setEnhancedData(result.enhanced_cv_data as CvDocument);
        setPhase("done");
        if (result.has_enhancement_logs) setHasLogs(true);
        onEnhanced?.({
          has_enhancement: true,
          cv_id_enhanced: cvId,
          enhanced_cv_data: result.enhanced_cv_data,
          has_enhancement_logs: result.has_enhancement_logs ?? hasLogs,
        });
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setError(err?.response?.data?.detail ?? "Enhancement failed. Please try again.");
        setPhase("error");
      }
    }
  }

  async function reEnhance() {
    const cvId = (selectedCvId as number) || cvIdEnhanced;
    if (!cvId) return;
    await runEnhancement(cvId);
  }

  const canEnhance = selectedCvId !== "" && (!isAdmin || !!employeeCode);

  useImperativeHandle(ref, () => ({
    runEnhancement: () => runEnhancement(selectedCvId as number),
    canEnhance,
  }));

  if (phase === "done" && enhancedData) {
    return (
      <>
        <div className="flex items-center gap-2 flex-wrap">
          <FileText className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
          <button
            onClick={() => setShowCvModal(true)}
            className="text-xs text-emerald-400 hover:text-emerald-300 font-inter font-medium underline underline-offset-2 transition-colors"
          >
            View Enhanced CV
          </button>
          {hasLogs && (
            <button
              onClick={() => setShowLogsModal(true)}
              className="text-xs text-muted-foreground hover:text-primary font-inter underline underline-offset-2 transition-colors"
            >
              Enhancement logs
            </button>
          )}
          <button
            onClick={reEnhance}
            title="Re-enhance"
            className="flex items-center gap-1 px-1.5 py-0.5 text-muted-foreground hover:text-primary rounded transition-colors"
          >
            <RotateCcw className="w-3 h-3 shrink-0" />
            <span className="text-[10px] font-inter underline underline-offset-2">Re-Enhance</span>
          </button>
        </div>
        {showCvModal && (
          <EnhancedCvModal
            enhancedData={enhancedData}
            jobTitle={jobTitle}
            jobDescription={jobDescription}
            cvId={(selectedCvId as number) || cvIdEnhanced || undefined}
            isAdmin={isAdmin}
            employeeCode={isAdmin ? employeeCode : undefined}
            onClose={() => setShowCvModal(false)}
          />
        )}
        {showLogsModal && (
          <EnhancementLogsModal
            assignmentId={assignmentId}
            isAdmin={isAdmin}
            onClose={() => setShowLogsModal(false)}
          />
        )}
      </>
    );
  }

  if (phase === "enhancing") {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground font-inter">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0" />
        Enhancing CV… this may take a minute
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="flex items-center gap-1.5 text-xs text-primary font-inter font-medium shrink-0">
          <Sparkles className="w-3.5 h-3.5 shrink-0" />
          {showOwnTrigger ? "Enhance CV for this job" : "CV"}
        </span>
        <select
          value={String(selectedCvId)}
          onChange={(e) => setSelectedCvId(e.target.value ? Number(e.target.value) : "")}
          disabled={cvLoading || (isAdmin && !employeeCode)}
          className="bg-surface-muted border border-border rounded-lg px-2 py-1 text-xs text-foreground font-inter cursor-pointer hover:border-border focus:outline-none focus:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">
            {cvLoading
              ? "Loading CVs…"
              : isAdmin && !employeeCode
              ? "— no employee linked —"
              : cvList?.length === 0
              ? "No CVs found"
              : "— select CV —"}
          </option>
          {(cvList ?? []).map((cv) => (
            <option key={cv.id} value={String(cv.id)}>
              {cv.title}
            </option>
          ))}
        </select>
        {showOwnTrigger && (
          <button
            onClick={() => runEnhancement(selectedCvId as number)}
            disabled={!canEnhance}
            className="px-2.5 py-1 bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground text-xs font-inter font-medium rounded-lg transition-colors shrink-0"
          >
            Enhance
          </button>
        )}
      </div>
      {error && <p className="text-[11px] text-destructive font-inter mt-1">{error}</p>}
    </>
  );
});

export default CvEnhancementSection;
